import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Buildings, House, ArrowRight, MagnifyingGlass, MapPin,
  ShieldCheck, Sparkle, HandCoins,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { useLocation as useZyroLocation } from "../context/LocationContext";
import HotelCard from "../components/HotelCard";
import PropertyCard from "../components/PropertyCard";

export default function HomePage() {
  const nav = useNavigate();
  const { city, detect, detecting } = useZyroLocation();
  const [hotels, setHotels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("hotels");

  useEffect(() => {
    api.get("/hotels").then(({ data }) => setHotels(data.slice(0, 3)));
    api.get("/properties").then(({ data }) => setRooms(data.slice(0, 3)));
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const path = mode === "hotels" ? "/hotels" : "/rentals";
    nav(`${path}?q=${encodeURIComponent(q)}`);
  };

  return (
    <>
      <section className="relative overflow-hidden bg-midnight text-bone" data-testid="hero-section">
        <div className="absolute inset-0 opacity-25" style={{
          backgroundImage: "url(https://images.pexels.com/photos/29602700/pexels-photo-29602700.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          backgroundSize: "cover", backgroundPosition: "center",
        }} />
        <div className="absolute inset-0 bg-gradient-to-b from-midnight/60 via-midnight/70 to-midnight" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-16 text-center">
          <div className="flex items-center justify-center mb-6" data-testid="big-logo">
            <img src="/zyro-logo.png" alt="Zyro — Connect. Grow. Earn." className="w-full max-w-2xl mx-auto" />
          </div>
          <p className="text-xs md:text-sm tracking-[0.5em] uppercase text-gold mb-8" data-testid="hero-tagline">Hotels · Rentals · Verified in India</p>
          <p className="text-bone/80 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Book a short stay or find a long-term rental — one trusted platform for every kind of home away from home.
          </p>

          <div className="max-w-2xl mx-auto mb-4">
            <div className="inline-flex glass-dark rounded-full p-1 gap-1 mb-4" role="tablist">
              <button onClick={() => setMode("hotels")} data-testid="mode-hotels"
                className={`px-6 py-2 rounded-full text-xs tracking-widest uppercase transition-colors ${mode === "hotels" ? "bg-bone text-midnight" : "text-bone/60 hover:text-bone"}`}>
                Hotels (Daily)
              </button>
              <button onClick={() => setMode("rentals")} data-testid="mode-rentals"
                className={`px-6 py-2 rounded-full text-xs tracking-widest uppercase transition-colors ${mode === "rentals" ? "bg-bone text-midnight" : "text-bone/60 hover:text-bone"}`}>
                Rentals (Monthly)
              </button>
            </div>

            <form onSubmit={submit} className="glass rounded-full p-2 flex items-center gap-2 bg-white/95" data-testid="hero-search">
              <div className="flex items-center gap-2 px-4 border-r border-black/10">
                <MapPin size={18} weight="duotone" className="text-gold" />
                <span className="text-sm text-midnight font-medium hidden sm:inline">{city}</span>
              </div>
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={mode === "hotels" ? "Search hotels or cities..." : "Search rentals — 1BHK, PG, locality..."}
                className="flex-1 bg-transparent px-3 py-2 text-midnight placeholder:text-neutral-500 outline-none text-sm"
                data-testid="hero-search-input" />
              <button type="submit" className="px-6 py-3 rounded-full bg-midnight text-bone text-sm flex items-center gap-2 hover:bg-midnight-hover" data-testid="hero-search-btn">
                <MagnifyingGlass size={16} weight="bold" /><span className="hidden sm:inline">Search</span>
              </button>
            </form>
          </div>

          <button onClick={detect} className="text-sm text-bone/70 hover:text-gold underline underline-offset-4" data-testid="use-location-btn">
            {detecting ? "Detecting..." : "📍 Use my current location"}
          </button>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link to="/hotels" className="group relative overflow-hidden rounded-3xl p-10 md:p-12 bg-gradient-to-br from-bone to-stone text-midnight hover:-translate-y-1 transition-transform duration-300 shadow-2xl" data-testid="cta-hotels">
              <div className="absolute top-6 right-6 opacity-10 group-hover:opacity-20 transition-opacity"><Buildings size={140} weight="duotone" /></div>
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-midnight text-bone flex items-center justify-center mb-6"><Buildings size={28} weight="duotone" /></div>
                <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Short Stay · Daily Booking</p>
                <h2 className="font-serif text-4xl md:text-5xl mb-3">Book a Hotel</h2>
                <p className="text-sm text-neutral-600 mb-8 max-w-sm leading-relaxed">
                  Boutique hotels, resorts, and heritage stays across India. Daily rates, instant confirmation.
                </p>
                <span className="inline-flex items-center gap-2 text-sm tracking-widest uppercase font-medium group-hover:gap-3 transition-all">
                  Explore hotels <ArrowRight size={18} />
                </span>
              </div>
            </Link>

            <Link to="/rentals" className="group relative overflow-hidden rounded-3xl p-10 md:p-12 bg-gradient-to-br from-gold to-gold-hover text-midnight hover:-translate-y-1 transition-transform duration-300 shadow-2xl" data-testid="cta-rentals">
              <div className="absolute top-6 right-6 opacity-15 group-hover:opacity-25 transition-opacity"><House size={140} weight="duotone" /></div>
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-midnight text-gold flex items-center justify-center mb-6"><House size={28} weight="duotone" /></div>
                <p className="text-xs tracking-[0.3em] uppercase text-midnight/70 mb-3">Long Stay · Monthly Rent</p>
                <h2 className="font-serif text-4xl md:text-5xl mb-3">Rent a Room</h2>
                <p className="text-sm text-midnight/80 mb-8 max-w-sm leading-relaxed">
                  Verified 1BHK, 2BHK, single rooms, PGs and studios. Monthly rent, transparent deposits.
                </p>
                <span className="inline-flex items-center gap-2 text-sm tracking-widest uppercase font-medium group-hover:gap-3 transition-all">
                  Find a room <ArrowRight size={18} />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
        {[
          { icon: ShieldCheck, title: "ID-Verified Community", body: "Every tenant and host verified via Aadhaar & Voter ID for safe transactions." },
          { icon: HandCoins, title: "Secure Payments", body: "Rent + deposit held safely, released to owner after check-in. UPI, cards, netbanking." },
          { icon: Sparkle, title: "Handpicked Homes", body: "Editors review every hotel & rental listing before it goes live on Zyro." },
        ].map((v, i) => {
          const Icon = v.icon;
          return (
            <div key={i} className="flex gap-5" data-testid={`value-${i}`}>
              <Icon size={28} weight="duotone" className="text-gold shrink-0" />
              <div>
                <h3 className="font-serif text-xl mb-2 text-midnight">{v.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{v.body}</p>
              </div>
            </div>
          );
        })}
      </section>

      {/* Featured Hotels */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-2">Short stays</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-midnight">Featured hotels</h2>
          </div>
          <Link to="/hotels" data-testid="see-all-hotels" className="text-sm tracking-widest uppercase text-midnight hover:text-gold flex items-center gap-2">See all <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger">
          {hotels.map((h) => <HotelCard key={h.id} hotel={h} />)}
        </div>
      </section>

      {/* Featured Rentals */}
      <section className="bg-stone py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-gold mb-2">Long stays</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-midnight">Rooms for rent</h2>
            </div>
            <Link to="/rentals" data-testid="see-all-rentals" className="text-sm tracking-widest uppercase text-midnight hover:text-gold flex items-center gap-2">See all <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger">
            {rooms.map((r) => <PropertyCard key={r.id} property={r} />)}
          </div>
        </div>
      </section>

      {/* Partner CTAs */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-10 rounded-3xl bg-midnight text-bone" data-testid="partner-hotel-cta">
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">For Hoteliers</p>
            <h3 className="font-serif text-3xl mb-4">List your hotel on Zyro</h3>
            <p className="text-neutral-400 mb-6 text-sm leading-relaxed">
              Reach millions of curated travellers. Zero setup fee, transparent pricing, dedicated success team.
            </p>
            <Link to="/register?role=hotel_partner" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-midnight hover:bg-gold-hover transition-colors text-sm font-medium">
              Become a hotel partner <ArrowRight size={16} />
            </Link>
          </div>
          <div className="p-10 rounded-3xl bg-white border border-black/5" data-testid="partner-room-cta">
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">For Room Owners</p>
            <h3 className="font-serif text-3xl mb-4 text-midnight">Rent out your empty room</h3>
            <p className="text-neutral-600 mb-6 text-sm leading-relaxed">
              List your 1BHK, 2BHK, single room or PG. Photos, monthly rent, tenant screening — all in one place.
            </p>
            <Link to="/register?role=room_partner" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-midnight text-bone hover:bg-midnight-hover transition-colors text-sm font-medium">
              Become a room owner <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
